import { matchPath } from 'react-router-dom';

/**
 * A single crumb in a breadcrumb trail.
 * `labelKey` is an i18n key — resolved at render time by the component using this.
 */
export interface BreadcrumbCrumb {
  labelKey: string;
  to?: string;
}

interface BreadcrumbRoute {
  pattern: string;
  crumbs: (params: Record<string, string | undefined>) => BreadcrumbCrumb[];
}

/**
 * Central breadcrumb registry. Order matters — more specific patterns must
 * come before less specific ones (e.g. `/cars/new` before `/cars/:id`).
 *
 * For routes with dynamic segments that need runtime data (e.g. car nickname),
 * the registry returns a placeholder crumb and the page component passes a
 * `crumbs` override to `<PageHeader />`.
 */
const ROUTES: BreadcrumbRoute[] = [
  // Top-level
  { pattern: '/dashboard', crumbs: () => [{ labelKey: 'nav.dashboard' }] },
  { pattern: '/explore', crumbs: () => [{ labelKey: 'nav.explore' }] },
  { pattern: '/events', crumbs: () => [{ labelKey: 'nav.events' }] },
  { pattern: '/bazaar', crumbs: () => [{ labelKey: 'nav.bazaar' }] },
  { pattern: '/forum', crumbs: () => [{ labelKey: 'nav.forum' }] },
  { pattern: '/garage', crumbs: () => [{ labelKey: 'nav.garage' }] },
  { pattern: '/profile', crumbs: () => [{ labelKey: 'nav.profile' }] },

  // Create/edit forms — most specific first
  {
    pattern: '/cars/new',
    crumbs: () => [
      { labelKey: 'nav.dashboard', to: '/dashboard' },
      { labelKey: 'newCar.title' },
    ],
  },
  {
    pattern: '/activities/new',
    crumbs: () => [
      { labelKey: 'nav.dashboard', to: '/dashboard' },
      { labelKey: 'newActivity.title' },
    ],
  },
  {
    pattern: '/bazaar/new',
    crumbs: () => [
      { labelKey: 'nav.bazaar', to: '/bazaar' },
      { labelKey: 'newBazaar.title' },
    ],
  },
  {
    pattern: '/forum/new',
    crumbs: () => [
      { labelKey: 'nav.forum', to: '/forum' },
      { labelKey: 'newForumThread.title' },
    ],
  },
  {
    pattern: '/forum/threads/:slugId',
    crumbs: () => [
      { labelKey: 'nav.forum', to: '/forum' },
      { labelKey: 'breadcrumbs.forumThread' },
    ],
  },

  // Detail pages — dynamic label is provided by the page via override prop
  {
    pattern: '/cars/:slugId/edit',
    crumbs: (params) => [
      { labelKey: 'nav.explore', to: '/explore' },
      { labelKey: 'breadcrumbs.car', to: `/cars/${params.slugId}` },
      { labelKey: 'editCar.title' },
    ],
  },
  {
    pattern: '/cars/:slugId',
    crumbs: () => [
      { labelKey: 'nav.explore', to: '/explore' },
      { labelKey: 'breadcrumbs.car' },
    ],
  },
  {
    pattern: '/events/:slugId',
    crumbs: () => [
      { labelKey: 'nav.events', to: '/events' },
      { labelKey: 'breadcrumbs.event' },
    ],
  },
  {
    pattern: '/bazaar/:slugId',
    crumbs: () => [
      { labelKey: 'nav.bazaar', to: '/bazaar' },
      { labelKey: 'breadcrumbs.bazaarItem' },
    ],
  },
  {
    pattern: '/users/:id',
    crumbs: () => [{ labelKey: 'breadcrumbs.user' }],
  },
  {
    pattern: '/garage/:slugId',
    crumbs: () => [
      { labelKey: 'nav.garage', to: '/garage' },
      { labelKey: 'breadcrumbs.car' },
    ],
  },
];

/**
 * Resolve a pathname to a breadcrumb trail from the registry.
 * Returns an empty array if the path isn't registered.
 */
export function resolveBreadcrumbs(pathname: string): BreadcrumbCrumb[] {
  for (const route of ROUTES) {
    const match = matchPath(route.pattern, pathname);
    if (match) {
      return route.crumbs(match.params as Record<string, string | undefined>);
    }
  }
  return [];
}
